import { getFilters } from '@/helpers/functions';
import { CacheManager } from '@/lib/cache';
import { DEFAULT_FILTERS_PAGE_SIZE } from '@/lib/config';
import { getDecisionTypeLabelStatic } from '@/lib/utils/decisionTypeLabels';
import filtersServices, {
  type FilterDefaultsResponse,
} from '@/services/filters-services';
import type {
  AreaTypes,
  CaseLegislationTypeInterface,
  CourtsType,
  DecisionTypeInterface,
  JudgesType,
  LegislationTypeInterface,
  LegislationTypeWithCountInterface,
  NatureType,
  ReleaseDateRangeInterface,
  SubAreaType,
  YearsRangeInterface,
} from '@/types/api-types';
import type { LegislationType } from '@/types/pdfTypes';
import {
  loadUserPreferences,
  saveApiDefaults,
  updateFiltersTimestamp,
} from '@/utils/filterLocalStorage';
import { PRE_HYDRATION_LEGISLATION_IN_FORCE_ONLY_DEFAULT } from '@/utils/legislationFilterDefaults';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

// ========================================
// Filter Caching Infrastructure
// ========================================
/**
 * In-memory cache for filter defaults to prevent redundant API calls
 * Key: 'filter-defaults'
 * Value: FilterDefaultsResponse
 *
 * Cache is automatically cleared on page refresh/navigation
 * TTL: 10 minutes (filter data changes less frequently)
 *
 * This works alongside the localStorage 24-hour cache:
 * - localStorage: Long-term persistence (survives page refresh)
 * - In-memory: Short-term optimization (cleared on page refresh)
 */
// Helper type for API response metadata structure
interface ApiMetadata {
  total?: number;
  limit?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  [key: string]: unknown;
}

export interface FilterOptionsData {
  decisions: {
    areas: AreaTypes[];
    courts: CourtsType[];
    judges: JudgesType[];
    natures: NatureType[];
    subAreas: SubAreaType[];
    caseLegislationTypes: Array<{ id: string; name: string }>;
    // Issue #908: Decision types with document counts for dynamic shimmer display
    decisionTypes?: DecisionTypeInterface[];
  };
  legislation: {
    legislationTypes: LegislationType[];
    yearRange: YearsRangeInterface;
    activeOnly: boolean;
    legislationTypesWithCounts?: LegislationTypeWithCountInterface[];
    effectiveAsOf?: string;
  };
  meta: {
    areas?: ApiMetadata;
    courts?: ApiMetadata;
    judges?: ApiMetadata;
    natures?: ApiMetadata;
    subAreas?: ApiMetadata;
    caseLegislationTypes?: ApiMetadata;
    legislationTypes?: ApiMetadata;
  };
}

const FILTER_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const filterDefaultsCache = new CacheManager<FilterDefaultsResponse>(
  'FilterDefaults',
  FILTER_CACHE_TTL
);

export const filterOptionsCache = new CacheManager<FilterOptionsData>(
  'FilterOptions',
  FILTER_CACHE_TTL
);

// NEW: Interface for filter items with ID and name
export interface FilterItemWithName {
  id: number;
  name: string;
}

export interface StringFilterItemWithName {
  id: string;
  name: string;
}

export enum higherFiltersEnum {
  legislation = 'legislation',
  decisions = 'decisions',
}

export enum lowerFiltersDecisionsEnum {
  areas = 'areas',
  subAreas = 'subAreas',
  natures = 'natures',
  judges = 'judges',
  courts = 'courts',
  caseLegislationTypes = 'caseLegislationTypes',
  decisionTypes = 'decisionTypes',
}

export enum lowerFiltersLegislationEnum {
  legislationTypes = 'legislationTypes',
  yearRange = 'yearRange',
  activeOnly = 'activeOnly',
  legislationInForceOnly = 'legislationInForceOnly',
  effectiveAsOf = 'effectiveAsOf',
}

// Type guard to check if array contains StringFilterItemWithName objects
function isStringFilterItemArray(
  arr: unknown[]
): arr is StringFilterItemWithName[] {
  return (
    arr.length > 0 &&
    typeof arr[0] === 'object' &&
    arr[0] !== null &&
    'id' in arr[0] &&
    'name' in arr[0] &&
    typeof (arr[0] as Record<string, unknown>)['id'] === 'string' &&
    typeof (arr[0] as Record<string, unknown>)['name'] === 'string'
  );
}

// Pagination state for each filter category
interface PaginationState {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  hasMore: boolean;
  loading: boolean;
}

// Default pagination settings
const defaultPagination: PaginationState = {
  page: 1,
  page_size: DEFAULT_FILTERS_PAGE_SIZE,
  total: 0,
  total_pages: 1,
  hasMore: true,
  loading: false,
};

interface FiltersInterface {
  defaultSpecialFilters?:
    | {
        yearRange?: YearsRangeInterface | undefined;
        activeOnly?: boolean | undefined;
        legislationInForceOnly?: boolean | undefined;
        effectiveAsOf?: string | undefined;
      }
    | undefined;
  defaultDecisionFilters?:
    | {
        decisionTypes?: StringFilterItemWithName[] | undefined;
        releaseDateRange?: ReleaseDateRangeInterface | undefined;
      }
    | undefined;
  defaultSearchScope?:
    | {
        searchDecisions: boolean;
        searchLegislation: boolean;
      }
    | undefined;
  searchDecisions?: boolean | undefined;
  searchLegislation?: boolean | undefined;
  decisions?:
    | {
        areas?: AreaTypes[] | undefined;
        subAreas?: SubAreaType[] | undefined;
        natures?: NatureType[] | undefined;
        judges?: JudgesType[] | undefined;
        courts?: CourtsType[] | undefined;
        caseLegislationTypes?: CaseLegislationTypeInterface[] | undefined;
        decisionTypes?: DecisionTypeInterface[] | undefined;
        releaseDateRange?: ReleaseDateRangeInterface | undefined;
        jurisdictions?: string[] | undefined;
      }
    | undefined;
  legislation?:
    | {
        legislationTypes?: LegislationTypeInterface[] | undefined;
        yearRange?: YearsRangeInterface | undefined;
        activeOnly?: boolean | undefined;
        legislationInForceOnly?: boolean | undefined;
        effectiveAsOf?: string | undefined;
        legislationTypesWithCounts?:
          | LegislationTypeWithCountInterface[]
          | undefined;
        jurisdictions?: string[] | undefined;
      }
    | undefined;
  appliedFilters?:
    | {
        decisions?:
          | {
              areas?: FilterItemWithName[] | undefined;
              courts?: FilterItemWithName[] | undefined;
              judges?: FilterItemWithName[] | undefined;
              natures?: FilterItemWithName[] | undefined;
              subAreas?: FilterItemWithName[] | undefined;
              caseLegislationTypes?: StringFilterItemWithName[] | undefined;
              decisionTypes?: StringFilterItemWithName[] | undefined;
              releaseDateRange?: ReleaseDateRangeInterface | undefined;
              jurisdictions?: string[] | undefined;
              // New orthogonal taxonomy fields from /filters/decision-kinds
              institutionalCourts?: string[] | undefined;
              publishedOnly?: boolean | undefined;
              // Issue #3724 phase 3: unified court hierarchy selection.
              // Holds two token kinds:
              //   * Axis tokens (`ustavny-sud-sr`, `krajsky-sud`, …) —
              //     select a whole bucket (server reads `court_axis`).
              //   * `court:<id>` tokens — individual SAK courts
              //     (server reads `court_ids`).
              // Reducer `toggleCourtSelection` is the only mutator; the
              // CourtHierarchyFilter component is the only producer.
              courtSelection?: string[] | undefined;
            }
          | undefined;
        legislation?:
          | {
              legislationTypes?: FilterItemWithName[] | undefined;
              yearRange?: YearsRangeInterface | undefined;
              activeOnly?: boolean | undefined;
              legislationInForceOnly?: boolean | undefined;
              effectiveAsOf?: string | undefined;
              jurisdictions?: string[] | undefined;
            }
          | undefined;
      }
    | undefined;
  totalSelectedFilters?: number | undefined;
  totalAvailableOptions?: number | undefined;
  filtersLoaded?: boolean | undefined;
  defaultsLoading?: boolean | undefined;
  hasUserInteracted?: boolean | undefined; // Track if user has actually changed filters
  // Issue #428: Auto-filter state
  autoFilterEnabled?: boolean | undefined; // Toggle state for Smart filter
  autoFilterLoading?: boolean | undefined; // Loading state for auto-filter API call (smart filter ON)
  rewriteLoading?: boolean | undefined; // Loading state for rewrite step (smart filter OFF)
  aiSuggestedFilters?:
    | {
        searchLegislation: boolean;
        searchDecisions: boolean;
        decisionTypes: string[];
        confidence: number;
        reasoning: string;
        filtersActuallyChanged: boolean; // True only if smart filter changed any settings
      }
    | null
    | undefined;
  originalFiltersBeforeAI?:
    | {
        // For "Revert" functionality
        searchLegislation: boolean;
        searchDecisions: boolean;
        decisionTypes: StringFilterItemWithName[];
      }
    | null
    | undefined;
  // Pagination state for each filter category
  pagination?:
    | {
        decisions?:
          | {
              [key in lowerFiltersDecisionsEnum]?: PaginationState | undefined;
            }
          | undefined;
        legislation?:
          | {
              legislationTypes?: PaginationState | undefined;
            }
          | undefined;
      }
    | undefined;
}

// Helper function to restore activeOnly from localStorage
// Returns undefined if not in localStorage - defaults will come from API
const getInitialActiveOnly = (): boolean | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    const storedActiveOnly = localStorage.getItem('legislationActiveOnly');
    return storedActiveOnly !== null ? storedActiveOnly === 'true' : undefined;
  } catch (error) {
    console.error('Error accessing localStorage for activeOnly:', error);
    return undefined;
  }
};

const initialState: FiltersInterface = {
  defaultSpecialFilters: {
    yearRange: undefined, // Will be set during initialization from API
    activeOnly: undefined, // Will be set during initialization from API
    legislationInForceOnly: undefined, // Will be set during initialization from API
    effectiveAsOf: undefined, // Will be set during initialization from API
  },
  defaultDecisionFilters: {
    decisionTypes: undefined, // Will be set during initialization from API
    releaseDateRange: undefined, // Will be set during initialization from API
  },
  defaultSearchScope: undefined, // Will be set during initialization from API
  searchDecisions: undefined, // Will be set during initialization from API
  searchLegislation: undefined, // Will be set during initialization from API
  decisions: {
    areas: [],
    courts: [],
    judges: [],
    natures: [],
    subAreas: [],
    caseLegislationTypes: [],
    decisionTypes: [],
    releaseDateRange: undefined,
  },
  legislation: {
    legislationTypes: [],
    yearRange: undefined,
    activeOnly: getInitialActiveOnly(), // Restore from localStorage
    legislationInForceOnly: undefined,
    effectiveAsOf: undefined,
  },
  appliedFilters: {
    decisions: {
      areas: [],
      courts: [],
      judges: [],
      natures: [],
      subAreas: [],
      caseLegislationTypes: [],
      decisionTypes: [],
      releaseDateRange: undefined,
      institutionalCourts: [],
      publishedOnly: false,
    },
    legislation: {
      legislationTypes: [],
      yearRange: undefined,
      activeOnly: getInitialActiveOnly(), // Restore from localStorage
      legislationInForceOnly: undefined,
      effectiveAsOf: undefined,
    },
  },
  totalSelectedFilters: 0,
  totalAvailableOptions: 0,
  filtersLoaded: false,
  defaultsLoading: true, // Start as true to prevent premature localStorage writes before API defaults load
  hasUserInteracted: false, // Start as false - only set to true when user changes filters
  // Issue #428: Auto-filter initial state
  autoFilterEnabled: true,
  autoFilterLoading: false,
  rewriteLoading: false,
  aiSuggestedFilters: null,
  originalFiltersBeforeAI: null,
  pagination: {
    decisions: {
      areas: { ...defaultPagination },
      courts: { ...defaultPagination },
      judges: { ...defaultPagination },
      natures: { ...defaultPagination },
      subAreas: { ...defaultPagination },
      caseLegislationTypes: { ...defaultPagination },
      decisionTypes: { ...defaultPagination },
    },
    legislation: {
      legislationTypes: { ...defaultPagination },
    },
  },
};

// Helper function to calculate total selected filters including special filters
// Counting rules:
// - searchLegislation: +1 if ON, 0 if OFF
// - searchDecisions: NOT counted directly, but controls whether decisionTypes/releaseDateRange are counted
// - decisionTypes: each selected type counts +1 (only when searchDecisions is ON)
// - effectiveAsOf: +1 if differs from API default
// - yearRange: +1 for each date that differs from API default (max +2)
// - activeOnly: +1 if differs from API default
// - releaseDateRange: +1 for each date that differs from API default (max +2, only when searchDecisions is ON)
/** Normalizes a date to YYYY-MM-DD for comparison. */
const normalizeDate = (d: string | undefined | null): string =>
  d ? (d.split('T')[0] ?? '') : '';

/** Today's date in YYYY-MM-DD format. */
const getTodayDate = (): string => new Date().toISOString().split('T')[0] ?? '';

/**
 * Resolves the "today" sentinel to the actual date, so a sentinel-valued
 * filter compares equal to an explicit today's-date default instead of
 * counting as a deviation.
 */
const resolveTodaySentinel = (d: string | undefined | null): string => {
  if (!d) return '';
  if (d === 'today') return getTodayDate();
  return d.split('T')[0] ?? '';
};

/**
 * Decision sub-filters that are NOT counted by plain array length — each has
 * its own rule below (gated on `searchDecisions`, or counted as a deviation
 * from the API default).
 */
const SEPARATELY_COUNTED_DECISION_KEYS = new Set([
  'decisionTypes',
  'releaseDateRange',
  'institutionalCourts',
  'publishedOnly',
  'jurisdictions',
  'courtSelection',
]);

/**
 * Counts the plain multi-select decision filters — every key except those in
 * {@link SEPARATELY_COUNTED_DECISION_KEYS}. Each selected item counts +1.
 */
function countPlainDecisionFilters(decisions: object | undefined): number {
  if (!decisions) {
    return 0;
  }
  let total = 0;
  for (const [key, subTabFilters] of Object.entries(decisions)) {
    if (SEPARATELY_COUNTED_DECISION_KEYS.has(key)) {
      continue;
    }
    if (Array.isArray(subTabFilters)) {
      total += subTabFilters.length;
    }
  }
  return total;
}

/**
 * Counts the decision filters gated on `searchDecisions` — court-hierarchy
 * tokens, jurisdictions, decision types and the publishedOnly toggle. All are
 * hidden in the UI when the decisions scope is off, so none of them count
 * then (matches the gated UI).
 *
 * Issue #3724 phase 3 — every token in the unified court hierarchy selection
 * counts as one filter. It mixes axis tokens and `court:<id>` tokens; both
 * narrow the result set, so both contribute to the badge.
 */
function countGatedDecisionFilters(state: FiltersInterface): number {
  if (!state.searchDecisions) {
    return 0;
  }
  const decisions = state.appliedFilters?.decisions;
  if (!decisions) {
    return 0;
  }
  const tokens =
    (decisions.institutionalCourts ?? []).length +
    (decisions.jurisdictions ?? []).length +
    (decisions.courtSelection ?? []).length +
    (decisions.decisionTypes ?? []).length;
  // publishedOnly: +1 if true (non-default)
  return tokens + (decisions.publishedOnly === true ? 1 : 0);
}

/** Counts the legislation multi-selects (types + jurisdictions). */
function countLegislationFilters(state: FiltersInterface): number {
  const legislation = state.appliedFilters?.legislation;
  return (
    (legislation?.legislationTypes?.length ?? 0) +
    (legislation?.jurisdictions?.length ?? 0)
  );
}

/**
 * Counts the legislation "special" filters, each +1 when it deviates from the
 * API default: effectiveAsOf, both ends of yearRange (max +2), activeOnly and
 * legislationInForceOnly.
 */
function countLegislationDeviations(state: FiltersInterface): number {
  const applied = state.appliedFilters?.legislation;
  const defaults = state.defaultSpecialFilters;
  let total = 0;

  if (
    resolveTodaySentinel(applied?.effectiveAsOf) !==
    resolveTodaySentinel(defaults?.effectiveAsOf)
  ) {
    total += 1;
  }
  if (
    normalizeDate(applied?.yearRange?.earliest_publication_date) !==
    normalizeDate(defaults?.yearRange?.earliest_publication_date)
  ) {
    total += 1;
  }
  if (
    resolveTodaySentinel(applied?.yearRange?.latest_publication_date) !==
    resolveTodaySentinel(defaults?.yearRange?.latest_publication_date)
  ) {
    total += 1;
  }
  if (applied?.activeOnly !== defaults?.activeOnly) {
    total += 1;
  }
  if (applied?.legislationInForceOnly !== defaults?.legislationInForceOnly) {
    total += 1;
  }
  return total;
}

/**
 * Counts releaseDateRange deviations from the API default (+1 per end that
 * differs, max +2). Only counted when the decisions scope is on.
 */
function countReleaseDateDeviations(state: FiltersInterface): number {
  if (!state.searchDecisions) {
    return 0;
  }
  const applied = state.appliedFilters?.decisions?.releaseDateRange;
  const defaults = state.defaultDecisionFilters?.releaseDateRange;
  let total = 0;

  if (
    normalizeDate(applied?.earliest_release_date) !==
    normalizeDate(defaults?.earliest_release_date)
  ) {
    total += 1;
  }
  if (
    resolveTodaySentinel(applied?.latest_release_date) !==
    resolveTodaySentinel(defaults?.latest_release_date)
  ) {
    total += 1;
  }
  return total;
}

const calculateTotalSelectedFilters = (state: FiltersInterface): number =>
  countPlainDecisionFilters(state.appliedFilters?.decisions) +
  countGatedDecisionFilters(state) +
  countLegislationFilters(state) +
  // searchLegislation ON = +1; searchDecisions is deliberately NOT counted
  // directly (the user can toggle it without moving the badge).
  (state.searchLegislation ? 1 : 0) +
  countLegislationDeviations(state) +
  countReleaseDateDeviations(state);

/** Merges pagination updates into one decision filter category. */
const updateDecisionPagination = (
  state: FiltersInterface,
  subTab: string,
  updateData: Partial<PaginationState>
): void => {
  if (!state.pagination) return;
  if (!state.pagination.decisions) {
    state.pagination.decisions = {};
  }
  const key = subTab as lowerFiltersDecisionsEnum;
  state.pagination.decisions[key] = {
    ...(state.pagination.decisions[key] || defaultPagination),
    ...updateData,
  };
};

/**
 * Merges pagination updates into the legislation side. Only
 * `legislationTypes` is paginated there, so any other subTab is a no-op.
 */
const updateLegislationPagination = (
  state: FiltersInterface,
  subTab: string,
  updateData: Partial<PaginationState>
): void => {
  if (!state.pagination) return;
  if (!state.pagination.legislation) {
    state.pagination.legislation = {};
  }
  if (subTab !== 'legislationTypes') return;
  state.pagination.legislation.legislationTypes = {
    ...(state.pagination.legislation.legislationTypes || defaultPagination),
    ...updateData,
  };
};

// Helper function to update a specific filter's pagination state
const updatePaginationState = (
  state: FiltersInterface,
  tab: higherFiltersEnum,
  subTab: string,
  updateData: Partial<PaginationState>
): void => {
  if (!state.pagination) {
    state.pagination = {
      decisions: {},
      legislation: {},
    };
  }

  if (tab === higherFiltersEnum.decisions) {
    updateDecisionPagination(state, subTab, updateData);
  } else if (tab === higherFiltersEnum.legislation) {
    updateLegislationPagination(state, subTab, updateData);
  }
};

/**
 * Sums the per-category totals recorded in pagination state — the accurate
 * count of *available* filter options, as opposed to how many rows happen to
 * be loaded. Returns 0 before any pagination meta has arrived.
 */
function sumPaginationTotals(state: FiltersInterface): number {
  const pagination = state.pagination;
  if (!pagination) {
    return 0;
  }
  let total = 0;
  for (const entry of Object.values(pagination.decisions ?? {})) {
    if (entry?.total) {
      total += entry.total;
    }
  }
  return total + (pagination.legislation?.legislationTypes?.total ?? 0);
}

/**
 * Fallback count used before pagination meta exists: how many options are
 * actually loaded in the payload. `releaseDateRange` is skipped — it is a date
 * range, not a list of options.
 */
function countLoadedOptions(
  decisions: object | undefined,
  legislation: { legislationTypes?: unknown[] | undefined } | undefined
): number {
  const decisionCount = Object.entries(decisions ?? {})
    .filter(([key]) => key !== 'releaseDateRange')
    .reduce((acc, [, val]) => acc + (Array.isArray(val) ? val.length : 0), 0);
  return decisionCount + (legislation?.legislationTypes?.length || 0);
}

/**
 * Merges an incoming decisions payload over what is already in the store.
 * `releaseDateRange` is preserved when the payload omits it.
 */
function mergeDecisionFilters(
  incoming: FiltersInterface['decisions'],
  current: FiltersInterface['decisions']
) {
  return {
    areas: incoming?.areas ?? undefined,
    courts: incoming?.courts ?? undefined,
    judges: incoming?.judges ?? undefined,
    natures: incoming?.natures ?? undefined,
    subAreas: incoming?.subAreas ?? undefined,
    caseLegislationTypes: incoming?.caseLegislationTypes ?? undefined,
    decisionTypes: incoming?.decisionTypes ?? undefined,
    releaseDateRange:
      incoming?.releaseDateRange !== undefined
        ? incoming.releaseDateRange
        : current?.releaseDateRange,
  };
}

/**
 * Merges an incoming legislation payload over what is already in the store.
 *
 * Existing legislationTypes / yearRange / activeOnly / effectiveAsOf survive
 * when the payload omits them, so `setFilters` cannot clobber defaults or
 * previously-set special filters.
 */
function mergeLegislationFilters(
  incoming: FiltersInterface['legislation'],
  current: FiltersInterface['legislation']
) {
  return {
    legislationTypes: incoming?.legislationTypes ?? current?.legislationTypes,
    yearRange:
      incoming?.yearRange !== undefined
        ? incoming.yearRange
        : current?.yearRange,
    activeOnly:
      incoming?.activeOnly !== undefined
        ? incoming.activeOnly
        : current?.activeOnly,
    legislationInForceOnly:
      incoming?.legislationInForceOnly !== undefined
        ? incoming.legislationInForceOnly
        : current?.legislationInForceOnly,
    effectiveAsOf:
      incoming?.effectiveAsOf !== undefined
        ? incoming.effectiveAsOf
        : current?.effectiveAsOf,
    legislationTypesWithCounts:
      incoming?.legislationTypesWithCounts ??
      current?.legislationTypesWithCounts,
  };
}

/**
 * Appends (or replaces) paginated rows for one decision filter category.
 * `releaseDateRange` is skipped — it is not an array.
 */
function appendDecisionRows(
  state: FiltersInterface,
  subTab: string,
  data: Record<string, unknown>[],
  replace: boolean | undefined
): void {
  if (subTab === 'releaseDateRange' || !state.decisions) {
    return;
  }
  const key = subTab as keyof typeof state.decisions;
  const currentValue = state.decisions[key];
  // biome-ignore lint/suspicious/noExplicitAny: heterogeneous filter lists
  state.decisions[key] = (
    replace || !Array.isArray(currentValue) ? data : [...currentValue, ...data]
  ) as any;
}

/** Appends (or replaces) paginated legislation-type rows. */
function appendLegislationRows(
  state: FiltersInterface,
  data: Record<string, unknown>[],
  replace: boolean | undefined
): void {
  if (!state.legislation) {
    return;
  }
  // biome-ignore lint/suspicious/noExplicitAny: heterogeneous filter lists
  state.legislation.legislationTypes = (
    replace ? data : [...(state.legislation.legislationTypes || []), ...data]
  ) as any;
}

/**
 * Ensures `appliedFilters` and both of its sections exist, so callers can
 * assign into them without repeating the same three guards.
 */
function ensureAppliedFilterSections(state: FiltersInterface): void {
  if (!state.appliedFilters) {
    state.appliedFilters = { decisions: {}, legislation: {} };
  }
  if (!state.appliedFilters.legislation) {
    state.appliedFilters.legislation = {};
  }
  if (!state.appliedFilters.decisions) {
    state.appliedFilters.decisions = {};
  }
}

/**
 * Legislation special filters filled from a *truthy* default — an empty-string
 * or absent default does not count as a value to fill from.
 */
const TRUTHY_FILLED_LEGISLATION_KEYS = ['yearRange', 'effectiveAsOf'] as const;

/**
 * Legislation special filters filled whenever the default is defined — so an
 * explicit `false` default IS applied, unlike the truthy-filled keys above.
 */
const DEFINED_FILLED_LEGISLATION_KEYS = [
  'activeOnly',
  'legislationInForceOnly',
] as const;

/**
 * Fills legislation special filters that have no value yet from the API
 * defaults. Existing user selections are never overwritten.
 */
function fillMissingLegislationDefaults(state: FiltersInterface): void {
  const applied = state.appliedFilters?.legislation;
  const defaults = state.defaultSpecialFilters;
  if (!applied || !defaults) {
    return;
  }
  const target = applied as Record<string, unknown>;
  for (const key of TRUTHY_FILLED_LEGISLATION_KEYS) {
    if (applied[key] === undefined && defaults[key]) {
      target[key] = defaults[key];
    }
  }
  for (const key of DEFINED_FILLED_LEGISLATION_KEYS) {
    if (applied[key] === undefined && defaults[key] !== undefined) {
      target[key] = defaults[key];
    }
  }
}

/**
 * Fills missing decision defaults. An empty array means the user explicitly
 * cleared every selection, so only `undefined` counts as "missing".
 */
function fillMissingDecisionDefaults(state: FiltersInterface): void {
  const applied = state.appliedFilters?.decisions;
  if (!applied) {
    return;
  }
  if (
    applied.decisionTypes === undefined &&
    state.defaultDecisionFilters?.decisionTypes
  ) {
    applied.decisionTypes = state.defaultDecisionFilters.decisionTypes;
  }
  if (
    applied.releaseDateRange === undefined &&
    state.decisions?.releaseDateRange
  ) {
    applied.releaseDateRange = state.decisions.releaseDateRange;
  }
}

/** The legislation half of a persisted (localStorage) filter blob. */
interface PersistedLegislationFilters {
  legislationTypes?: FilterItemWithName[];
  yearRange?: YearsRangeInterface;
  activeOnly?: boolean;
  legislationInForceOnly?: boolean;
  effectiveAsOf?: string;
  jurisdictions?: string[];
}

/**
 * Decision keys whose persisted value is a list of `{ id, name }` items.
 * Anything not listed here (and not handled specially) is ignored on restore.
 */
const ITEM_LIST_DECISION_KEYS = new Set([
  'areas',
  'courts',
  'judges',
  'natures',
  'subAreas',
  'caseLegislationTypes',
  'decisionTypes',
]);

/**
 * Restores one decision filter entry from a persisted blob, dropping members
 * that don't match the expected shape.
 */
function restoreDecisionEntry(
  target: Record<string, unknown>,
  key: string,
  value: unknown
): void {
  // publishedOnly is a boolean field — handled outside the array check.
  if (key === 'publishedOnly') {
    if (typeof value === 'boolean') {
      target['publishedOnly'] = value;
    }
    return;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return;
  }
  // jurisdictions and institutionalCourts are plain string[].
  if (key === 'jurisdictions' || key === 'institutionalCourts') {
    target[key] = value.filter((v): v is string => typeof v === 'string');
    return;
  }
  if (!ITEM_LIST_DECISION_KEYS.has(key)) {
    return;
  }
  // Everything else is FilterItemWithName / StringFilterItemWithName.
  target[key] = (
    value as (FilterItemWithName | StringFilterItemWithName)[]
  ).filter((item) => item.id !== undefined && item.name !== undefined);
}

/** Restores the decisions half of a persisted filter blob. */
function restoreDecisionFilters(
  state: FiltersInterface,
  decisions: Record<string, unknown>
): void {
  ensureAppliedFilterSections(state);
  const target = state.appliedFilters?.decisions as
    | Record<string, unknown>
    | undefined;
  if (!target) {
    return;
  }
  for (const [key, value] of Object.entries(decisions)) {
    restoreDecisionEntry(target, key, value);
  }
}

/**
 * Writes one legislation special filter into appliedFilters and mirrors it
 * onto `state.legislation`, so the UI control reflects the restored value.
 */
function setLegislationSpecialFilter(
  state: FiltersInterface,
  key: 'yearRange' | 'activeOnly' | 'legislationInForceOnly' | 'effectiveAsOf',
  value: unknown
): void {
  const applied = state.appliedFilters?.legislation;
  if (!applied) {
    return;
  }
  (applied as Record<string, unknown>)[key] = value;
  if (state.legislation) {
    (state.legislation as Record<string, unknown>)[key] = value;
  }
}

/**
 * Restores the legislation half of a persisted filter blob.
 *
 * Note the two guard styles, preserved from the original: `yearRange` and
 * `effectiveAsOf` restore only on a truthy value, while the two booleans
 * restore whenever defined (so a persisted `false` is honoured).
 */
function restoreLegislationFilters(
  state: FiltersInterface,
  legislation: PersistedLegislationFilters
): void {
  ensureAppliedFilterSections(state);
  const applied = state.appliedFilters?.legislation;
  if (!applied) {
    return;
  }

  if (Array.isArray(legislation.legislationTypes)) {
    applied.legislationTypes = legislation.legislationTypes.filter(
      (item) => item.id !== undefined && item.name !== undefined
    );
  }
  if (legislation.yearRange) {
    setLegislationSpecialFilter(state, 'yearRange', legislation.yearRange);
  }
  if (legislation.activeOnly !== undefined) {
    setLegislationSpecialFilter(state, 'activeOnly', legislation.activeOnly);
  }
  if (legislation.legislationInForceOnly !== undefined) {
    setLegislationSpecialFilter(
      state,
      'legislationInForceOnly',
      legislation.legislationInForceOnly
    );
  }
  if (legislation.effectiveAsOf) {
    setLegislationSpecialFilter(
      state,
      'effectiveAsOf',
      legislation.effectiveAsOf
    );
  }
  if (Array.isArray(legislation.jurisdictions)) {
    applied.jurisdictions = legislation.jurisdictions.filter(
      (code): code is string => typeof code === 'string'
    );
  }
}

/**
 * Narrows the AI's suggested decision types against what the user had.
 *
 * The AI may only narrow, never expand: when the user had specific types the
 * result is the intersection; when the user had none (empty = all enabled),
 * the AI's selection applies as-is.
 */
function narrowDecisionTypeIds(
  originalTypeIds: string[],
  aiDecisionTypes: string[]
): string[] {
  if (originalTypeIds.length === 0) {
    return aiDecisionTypes;
  }
  return aiDecisionTypes.filter((typeId) => originalTypeIds.includes(typeId));
}

/** True when two decision-type id lists differ as sets (order-insensitive). */
function decisionTypeIdsDiffer(a: string[], b: string[]): boolean {
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return (
    sortedA.length !== sortedB.length ||
    !sortedA.every((id, idx) => id === sortedB[idx])
  );
}

/**
 * Applies the AI's decision-type narrowing to the store, converting the
 * surviving ids back into `{ id, name }` objects. Returns whether the
 * effective selection actually changed.
 */
function applyDecisionTypeNarrowing(
  state: FiltersInterface,
  originalDecisionTypes: StringFilterItemWithName[],
  aiDecisionTypes: string[]
): boolean {
  const decisions = state.appliedFilters?.decisions;
  if (!decisions) {
    return false;
  }
  const originalTypeIds = originalDecisionTypes.map((dt) => dt.id);
  const narrowedTypes = narrowDecisionTypeIds(originalTypeIds, aiDecisionTypes);
  decisions.decisionTypes = narrowedTypes.map((id) => ({
    id,
    name: getDecisionTypeLabelStatic(id),
  }));
  return decisionTypeIdsDiffer(narrowedTypes, originalTypeIds);
}

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FiltersInterface>) => {
      const { decisions, legislation } = action.payload;

      // Prefer pagination meta for the total; fall back to loaded array
      // lengths before any meta has arrived.
      let totalAvailableOptions = sumPaginationTotals(state);
      if (totalAvailableOptions === 0 && decisions && legislation) {
        totalAvailableOptions = countLoadedOptions(decisions, legislation);
      }

      return {
        ...state,
        // Don't reset search preferences - preserve existing values
        searchDecisions: state.searchDecisions,
        searchLegislation: state.searchLegislation,
        decisions: mergeDecisionFilters(decisions, state.decisions),
        legislation: mergeLegislationFilters(legislation, state.legislation),
        totalAvailableOptions,
        filtersLoaded: true,
      };
    },

    // New action to append paginated filter data
    appendFilterData: (
      state,
      action: PayloadAction<{
        tab: higherFiltersEnum;
        subTab: string;
        data: Record<string, unknown>[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
        replace?: boolean;
      }>
    ) => {
      const {
        tab,
        subTab,
        data,
        total,
        page,
        page_size,
        total_pages,
        replace,
      } = action.payload;

      // Update pagination state
      updatePaginationState(state, tab, subTab, {
        page,
        page_size,
        total,
        total_pages,
        hasMore: page < total_pages,
        loading: false,
      });

      // Append new data to existing filters
      if (tab === higherFiltersEnum.decisions) {
        appendDecisionRows(state, subTab, data, replace);
      } else if (
        tab === higherFiltersEnum.legislation &&
        subTab === 'legislationTypes'
      ) {
        appendLegislationRows(state, data, replace);
      }

      // Recalculate total available options whenever filter data is updated
      state.totalAvailableOptions = sumPaginationTotals(state);
    },

    // Set loading state for a specific filter category
    setFilterLoading: (
      state,
      action: PayloadAction<{
        tab: higherFiltersEnum;
        subTab: string;
        loading: boolean;
      }>
    ) => {
      const { tab, subTab, loading } = action.payload;
      updatePaginationState(state, tab, subTab, { loading });
    },

    // Reset pagination for a specific filter category
    resetFilterPagination: (
      state,
      action: PayloadAction<{
        tab: higherFiltersEnum;
        subTab: string;
      }>
    ) => {
      const { tab, subTab } = action.payload;
      updatePaginationState(state, tab, subTab, {
        ...defaultPagination,
      });
    },

    toggleFilterVisibility: (
      state,
      action: PayloadAction<{
        key: 'searchDecisions' | 'searchLegislation';
        value: boolean;
      }>
    ) => {
      const { key, value } = action.payload;
      state[key] = value;
      state.hasUserInteracted = true; // User changed search scope
      // Recalculate totalSelectedFilters since searchLegislation affects the count
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    toggleFilter: (
      state,
      action: PayloadAction<{
        tab: higherFiltersEnum;
        subTab: lowerFiltersDecisionsEnum | lowerFiltersLegislationEnum;
        item: FilterItemWithName | StringFilterItemWithName;
      }>
    ) => {
      const { tab, subTab, item } = action.payload;
      const currentFilters = state.appliedFilters?.[tab];

      if (!currentFilters) return;

      // Get current array for this filter type
      // @ts-expect-error - Dynamic key access is safe here
      const currentArray = currentFilters[subTab] as
        | Array<FilterItemWithName | StringFilterItemWithName>
        | undefined;

      // Check if item already exists (compare by ID)
      const exists = currentArray?.some((f) => f.id === item.id);

      // Toggle: remove if exists, add if doesn't
      const updatedSubTab = exists
        ? currentArray?.filter((f) => f.id !== item.id) || []
        : [...(currentArray || []), item];

      // Update applied filters
      state.appliedFilters = {
        ...state.appliedFilters,
        [tab]: {
          ...currentFilters,
          [subTab]: updatedSubTab,
        },
      };

      // Mark that user has interacted with filters
      state.hasUserInteracted = true;

      // Recalculate total selected filters using the helper function
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    toggleDecisionType: (
      state,
      action: PayloadAction<{
        item: StringFilterItemWithName;
      }>
    ) => {
      const { item } = action.payload;
      const currentFilters =
        state.appliedFilters?.decisions?.decisionTypes || [];

      // Check if item already exists (compare by ID)
      const exists = currentFilters.some((f) => f.id === item.id);

      // Toggle: remove if exists, add if doesn't
      const updatedDecisionTypes = exists
        ? currentFilters.filter((f) => f.id !== item.id)
        : [...currentFilters, item];

      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.decisionTypes = updatedDecisionTypes;

        // If all decision types are turned OFF (empty array), clear all other decision category filters
        if (updatedDecisionTypes.length === 0) {
          state.appliedFilters.decisions.areas = [];
          state.appliedFilters.decisions.courts = [];
          state.appliedFilters.decisions.judges = [];
          state.appliedFilters.decisions.natures = [];
          state.appliedFilters.decisions.subAreas = [];
          state.appliedFilters.decisions.caseLegislationTypes = [];
          state.appliedFilters.decisions.institutionalCourts = [];
          state.appliedFilters.decisions.publishedOnly = false;
        }
      }

      // Mark that user has interacted with filters
      state.hasUserInteracted = true;

      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setAllDecisionTypes: (
      state,
      action: PayloadAction<{
        items: StringFilterItemWithName[];
      }>
    ) => {
      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.decisionTypes = action.payload.items;
      }

      // Mark that user has interacted with filters
      state.hasUserInteracted = true;

      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Set the jurisdiction subset for legislation or decisions panel.
    // Empty array = "all jurisdictions" (no filter sent to backend) —
    // matches `($2::jsonb) -> '..._jurisdictions' IS NULL` short-circuit
    // in research_fulltext_search.sql.
    setJurisdictions: (
      state,
      action: PayloadAction<{
        tab: higherFiltersEnum;
        codes: string[];
      }>
    ) => {
      const { tab, codes } = action.payload;

      if (
        tab === higherFiltersEnum.legislation &&
        state.appliedFilters?.legislation
      ) {
        state.appliedFilters.legislation.jurisdictions = codes;
      } else if (
        tab === higherFiltersEnum.decisions &&
        state.appliedFilters?.decisions
      ) {
        state.appliedFilters.decisions.jurisdictions = codes;
      }

      state.hasUserInteracted = true;
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Toggle an institutional court bucket (court_axis value) on/off
    toggleInstitutionalCourt: (
      state,
      action: PayloadAction<{ courtAxisValue: string }>
    ) => {
      const { courtAxisValue } = action.payload;
      const current =
        state.appliedFilters?.decisions?.institutionalCourts ?? [];
      const next = current.includes(courtAxisValue)
        ? current.filter((c) => c !== courtAxisValue)
        : [...current, courtAxisValue];
      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.institutionalCourts = next;
      }
      state.hasUserInteracted = true;
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Issue #3724 phase 3: toggle one token in the unified court hierarchy
    // selection. Accepts both axis tokens (`ustavny-sud-sr`, `krajsky-sud`)
    // and individual-court tokens (`court:42`). See `AppliedFiltersType`
    // contract above and `CourtHierarchyFilter.tsx` for the producer side.
    toggleCourtSelection: (state, action: PayloadAction<{ token: string }>) => {
      const { token } = action.payload;
      const current = state.appliedFilters?.decisions?.courtSelection ?? [];
      const next = current.includes(token)
        ? current.filter((t) => t !== token)
        : [...current, token];
      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.courtSelection = next;
      }
      state.hasUserInteracted = true;
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Set publishedOnly toggle (true = only published, false = all)
    setPublishedOnly: (state, action: PayloadAction<boolean>) => {
      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.publishedOnly = action.payload;
      }
      state.hasUserInteracted = true;
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    clearAllFilters: (state) => {
      // Reset to API defaults (same as initial load without localStorage)
      state.appliedFilters = {
        decisions: {
          areas: [],
          courts: [],
          judges: [],
          natures: [],
          subAreas: [],
          caseLegislationTypes: [],
          // Reset decision types to API defaults instead of empty array
          decisionTypes: state.defaultDecisionFilters?.decisionTypes ?? [],
          // Reset release date range to API defaults
          releaseDateRange: state.defaultDecisionFilters?.releaseDateRange,
          // Reset jurisdiction filter to "all" (no restriction)
          jurisdictions: [],
          // Reset new orthogonal taxonomy fields
          institutionalCourts: [],
          publishedOnly: false,
        },
        legislation: {
          legislationTypes: [],
          // Reset special filter properties to their defaults from API
          effectiveAsOf: state.defaultSpecialFilters?.effectiveAsOf,
          yearRange: state.defaultSpecialFilters?.yearRange,
          activeOnly: state.defaultSpecialFilters?.activeOnly,
          legislationInForceOnly:
            state.defaultSpecialFilters?.legislationInForceOnly,
          // Reset jurisdiction filter to "all" (no restriction)
          jurisdictions: [],
        },
      };

      // Reset legislation state special filters to defaults from API
      if (state.legislation) {
        state.legislation.effectiveAsOf =
          state.defaultSpecialFilters?.effectiveAsOf;
        state.legislation.yearRange = state.defaultSpecialFilters?.yearRange;
        state.legislation.activeOnly = state.defaultSpecialFilters?.activeOnly;
        state.legislation.legislationInForceOnly =
          state.defaultSpecialFilters?.legislationInForceOnly;
      } else {
        // If state.legislation is not defined, initialize it with defaults from API
        state.legislation = {
          legislationTypes: [],
          effectiveAsOf: state.defaultSpecialFilters?.effectiveAsOf,
          yearRange: state.defaultSpecialFilters?.yearRange,
          activeOnly: state.defaultSpecialFilters?.activeOnly,
          legislationInForceOnly:
            state.defaultSpecialFilters?.legislationInForceOnly,
        };
      }

      // Reset searchDecisions and searchLegislation to API defaults
      // These values are stored in defaultSearchScope from the API
      if (state.defaultSearchScope) {
        state.searchDecisions = state.defaultSearchScope.searchDecisions;
        state.searchLegislation = state.defaultSearchScope.searchLegislation;
      }

      // Mark that user has interacted with filters
      state.hasUserInteracted = true;

      // Recalculate total selected filters after resetting
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setTotalSelectedFilters: (
      state,
      action: PayloadAction<{
        filters?: FiltersInterface['appliedFilters'];
        searchDecisions?: boolean;
        searchLegislation?: boolean;
      }>
    ) => {
      // Update filter state from payload
      if (action.payload.searchDecisions !== undefined) {
        state.searchDecisions = action.payload.searchDecisions;
      }
      if (action.payload.searchLegislation !== undefined) {
        state.searchLegislation = action.payload.searchLegislation;
      }
      if (action.payload.filters) {
        // Only update appliedFilters if provided in payload
        state.appliedFilters = {
          decisions:
            action.payload.filters.decisions ?? state.appliedFilters?.decisions,
          legislation:
            action.payload.filters.legislation ??
            state.appliedFilters?.legislation,
        };
      }

      // Recalculate total selected filters based on the potentially updated state
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setYearRange: (state, action: PayloadAction<YearsRangeInterface>) => {
      if (state.legislation) {
        state.legislation.yearRange = action.payload;
      }
      if (state.appliedFilters?.legislation) {
        state.appliedFilters.legislation.yearRange = action.payload;
      }
      // Mark that user has interacted with filters
      state.hasUserInteracted = true;
      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setReleaseDateRange: (
      state,
      action: PayloadAction<ReleaseDateRangeInterface>
    ) => {
      if (state.decisions) {
        state.decisions.releaseDateRange = action.payload;
      }
      if (state.appliedFilters?.decisions) {
        state.appliedFilters.decisions.releaseDateRange = action.payload;
      }
      // Mark that user has interacted with filters
      state.hasUserInteracted = true;
      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setActiveOnly: (state, action: PayloadAction<boolean>) => {
      // Ensure the value is a boolean regardless of what was passed
      const booleanValue = Boolean(action.payload);

      if (state.appliedFilters?.legislation) {
        state.appliedFilters.legislation.activeOnly = booleanValue;
      }

      if (state.legislation) {
        state.legislation.activeOnly = booleanValue;
      }

      // Try to save to localStorage for persistence
      try {
        localStorage.setItem('legislationActiveOnly', String(booleanValue));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
      // Mark that user has interacted with filters
      state.hasUserInteracted = true;
      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setLegislationInForceOnly: (state, action: PayloadAction<boolean>) => {
      const booleanValue = Boolean(action.payload);

      if (state.appliedFilters?.legislation) {
        state.appliedFilters.legislation.legislationInForceOnly = booleanValue;
      }

      if (state.legislation) {
        state.legislation.legislationInForceOnly = booleanValue;
      }

      // Mark that user has interacted with filters
      state.hasUserInteracted = true;
      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    setEffectiveAsOf: (state, action: PayloadAction<string | undefined>) => {
      const effectiveDate = action.payload; // Default to today if undefined
      if (state.legislation) {
        state.legislation.effectiveAsOf = effectiveDate;
      }
      if (state.appliedFilters?.legislation) {
        state.appliedFilters.legislation.effectiveAsOf = effectiveDate;
      }
      // Mark that user has interacted with filters
      state.hasUserInteracted = true;
      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // New reducer for defaultSpecialFilters
    setDefaultSpecialFilters: (
      state,
      action: PayloadAction<Partial<FiltersInterface['defaultSpecialFilters']>>
    ) => {
      state.defaultSpecialFilters = {
        ...state.defaultSpecialFilters,
        ...action.payload,
      };
      // Recalculate total selected filters as defaults have changed
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // New reducer for defaultDecisionFilters
    setDefaultDecisionFilters: (
      state,
      action: PayloadAction<Partial<FiltersInterface['defaultDecisionFilters']>>
    ) => {
      state.defaultDecisionFilters = {
        ...state.defaultDecisionFilters,
        ...action.payload,
      };
      // Recalculate total selected filters as defaults have changed
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Reducer to set default search scope from API
    setDefaultSearchScope: (
      state,
      action: PayloadAction<{
        searchDecisions: boolean;
        searchLegislation: boolean;
      }>
    ) => {
      state.defaultSearchScope = action.payload;
    },

    // Reducer to set the loading state for defaults API
    setDefaultsLoading: (state, action: PayloadAction<boolean>) => {
      state.defaultsLoading = action.payload;
    },

    // Reducer to fill in missing special filters with API defaults
    // This is called when localStorage exists but has partial data
    fillMissingSpecialFiltersWithDefaults: (state) => {
      ensureAppliedFilterSections(state);
      // Only fill in missing values — don't overwrite user selections.
      fillMissingLegislationDefaults(state);
      fillMissingDecisionDefaults(state);

      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // New reducer to apply default selections to appliedFilters and legislation state
    // This should only be called after defaults are fetched from the API
    applyDefaultSelections: (state) => {
      const defaults = state.defaultSpecialFilters;
      const decisionDefaults = state.defaultDecisionFilters;

      if (!state.appliedFilters) {
        state.appliedFilters = { decisions: {}, legislation: {} };
      }
      if (!state.appliedFilters.legislation) {
        state.appliedFilters.legislation = {};
      }
      if (!state.appliedFilters.decisions) {
        state.appliedFilters.decisions = {};
      }
      if (!state.legislation) {
        state.legislation = {};
      }

      // Apply defaults from API to appliedFilters.legislation
      state.appliedFilters.legislation.activeOnly = defaults?.activeOnly;
      state.appliedFilters.legislation.legislationInForceOnly =
        defaults?.legislationInForceOnly;
      state.appliedFilters.legislation.yearRange = defaults?.yearRange;
      state.appliedFilters.legislation.effectiveAsOf = defaults?.effectiveAsOf;
      // Ensure legislationTypes is initialized if not present
      if (!state.appliedFilters.legislation.legislationTypes) {
        state.appliedFilters.legislation.legislationTypes = [];
      }

      // Apply decision defaults from API to appliedFilters.decisions
      if (decisionDefaults?.decisionTypes) {
        state.appliedFilters.decisions.decisionTypes =
          decisionDefaults.decisionTypes;
      }

      // Apply releaseDateRange from defaultDecisionFilters to appliedFilters
      // Use defaultDecisionFilters as the source of truth for defaults
      if (decisionDefaults?.releaseDateRange) {
        state.appliedFilters.decisions.releaseDateRange =
          decisionDefaults.releaseDateRange;
      }

      // Also apply defaults from API to the main legislation state for consistency
      state.legislation.activeOnly = defaults?.activeOnly;
      state.legislation.legislationInForceOnly =
        defaults?.legislationInForceOnly;
      state.legislation.yearRange = defaults?.yearRange;
      state.legislation.effectiveAsOf = defaults?.effectiveAsOf;
      // Ensure legislationTypes is initialized if not present
      if (!state.legislation.legislationTypes) {
        state.legislation.legislationTypes = [];
      }

      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // New reducer to apply default values only to legislation state (for UI display)
    // This should only be called after defaults are fetched from the API
    // IMPORTANT: This does NOT overwrite appliedFilters.legislation - that should only be set by:
    // - applyDefaultSelections (when no localStorage exists)
    // - restoreFiltersFromLocalStorage (when localStorage exists)
    applyDefaultValuesToLegislationState: (state) => {
      const defaults = state.defaultSpecialFilters;

      if (!state.legislation) {
        state.legislation = {};
      }

      // Apply defaults from API ONLY to legislation state (for UI display of available options)
      // DO NOT touch appliedFilters.legislation - that contains user selections!
      state.legislation.activeOnly = defaults?.activeOnly;
      state.legislation.legislationInForceOnly =
        defaults?.legislationInForceOnly;
      state.legislation.yearRange = defaults?.yearRange;
      state.legislation.effectiveAsOf = defaults?.effectiveAsOf;

      // Ensure legislationTypes is initialized if not present
      if (!state.legislation.legislationTypes) {
        state.legislation.legislationTypes = [];
      }
    },

    // New reducer to restore filters from localStorage
    restoreFiltersFromLocalStorage: (
      state,
      action: PayloadAction<{
        searchDecisions?: boolean;
        searchLegislation?: boolean;
        decisions?: Record<
          string,
          (FilterItemWithName | StringFilterItemWithName)[] | string[] | boolean
        >;
        legislation?: {
          legislationTypes?: FilterItemWithName[];
          yearRange?: YearsRangeInterface;
          activeOnly?: boolean;
          legislationInForceOnly?: boolean;
          effectiveAsOf?: string;
          jurisdictions?: string[];
        };
      }>
    ) => {
      const { searchDecisions, searchLegislation, decisions, legislation } =
        action.payload;

      // Restore search preferences
      if (searchDecisions !== undefined) {
        state.searchDecisions = searchDecisions;
      }
      if (searchLegislation !== undefined) {
        state.searchLegislation = searchLegislation;
      }

      // Restore decision filters (full objects, not just IDs)
      if (decisions) {
        restoreDecisionFilters(state, decisions);
      }

      // Restore legislation filters
      if (legislation) {
        restoreLegislationFilters(state, legislation);
      }

      // Recalculate total selected filters
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    // Issue #428: Auto-filter reducers
    toggleAutoFilter: (state, action: PayloadAction<boolean>) => {
      state.autoFilterEnabled = action.payload;
      // Clear AI suggestions when disabled
      if (!action.payload) {
        state.aiSuggestedFilters = null;
        state.originalFiltersBeforeAI = null;
      }
    },

    setAutoFilterLoading: (state, action: PayloadAction<boolean>) => {
      state.autoFilterLoading = action.payload;
    },

    // Issue #428: Set rewrite loading state (for rewrite step when smart filter is OFF)
    setRewriteLoading: (state, action: PayloadAction<boolean>) => {
      state.rewriteLoading = action.payload;
    },

    applyAISuggestedFilters: (
      state,
      action: PayloadAction<{
        searchLegislation: boolean;
        searchDecisions: boolean;
        decisionTypes: string[];
        confidence: number;
        reasoning: string;
      }>
    ) => {
      const suggestion = action.payload;

      // Save original state for revert
      // For decisionTypes: use current applied types, or fall back to API defaults if empty/undefined
      // Empty array [] in applied means "no specific types selected" which equals "all defaults enabled"
      const currentDecisionTypes =
        state.appliedFilters?.decisions?.decisionTypes || [];
      const defaultDecisionTypes =
        state.defaultDecisionFilters?.decisionTypes || [];
      const originalSearchLegislation = state.searchLegislation ?? true;
      const originalSearchDecisions = state.searchDecisions ?? true;
      const originalDecisionTypes =
        currentDecisionTypes.length > 0
          ? currentDecisionTypes
          : defaultDecisionTypes;

      state.originalFiltersBeforeAI = {
        searchLegislation: originalSearchLegislation,
        searchDecisions: originalSearchDecisions,
        // If user has no specific selection (empty array), save the defaults so revert restores them
        decisionTypes: originalDecisionTypes,
      };

      // Track what will actually change
      let searchLegislationChanged = false;
      let searchDecisionsChanged = false;
      let decisionTypesChanged = false;

      // Apply overlay logic: AI can only narrow, not expand
      // If user had searchLegislation=false, keep it false even if AI suggests true
      if (originalSearchLegislation) {
        searchLegislationChanged =
          state.searchLegislation !== suggestion.searchLegislation;
        state.searchLegislation = suggestion.searchLegislation;
      }

      if (originalSearchDecisions) {
        searchDecisionsChanged =
          state.searchDecisions !== suggestion.searchDecisions;
        state.searchDecisions = suggestion.searchDecisions;

        // Apply decision type narrowing
        decisionTypesChanged = applyDecisionTypeNarrowing(
          state,
          originalDecisionTypes,
          suggestion.decisionTypes
        );
      }

      // Store AI suggestion with flag indicating if anything actually changed
      state.aiSuggestedFilters = {
        ...suggestion,
        filtersActuallyChanged:
          searchLegislationChanged ||
          searchDecisionsChanged ||
          decisionTypesChanged,
      };

      // Recalculate filter count
      state.totalSelectedFilters = calculateTotalSelectedFilters(state);
    },

    revertAIFilters: (state) => {
      if (state.originalFiltersBeforeAI) {
        state.searchLegislation =
          state.originalFiltersBeforeAI.searchLegislation;
        state.searchDecisions = state.originalFiltersBeforeAI.searchDecisions;

        // Ensure appliedFilters.decisions exists before restoring decisionTypes
        if (!state.appliedFilters) {
          state.appliedFilters = { decisions: {}, legislation: {} };
        }
        if (!state.appliedFilters.decisions) {
          state.appliedFilters.decisions = {};
        }
        state.appliedFilters.decisions.decisionTypes =
          state.originalFiltersBeforeAI.decisionTypes;

        state.aiSuggestedFilters = null;
        state.originalFiltersBeforeAI = null;
        state.totalSelectedFilters = calculateTotalSelectedFilters(state);
      }
    },

    clearAISuggestions: (state) => {
      state.aiSuggestedFilters = null;
      state.originalFiltersBeforeAI = null;
    },
  },
});

/** Pagination meta the filters API returns per filter category. */
interface FilterCategoryMeta {
  total?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  [key: string]: any;
}

/** The per-category meta block carried on the filters response. */
interface ExtendedFiltersMeta {
  areas?: FilterCategoryMeta;
  courts?: FilterCategoryMeta;
  judges?: FilterCategoryMeta;
  natures?: FilterCategoryMeta;
  subAreas?: FilterCategoryMeta;
  caseLegislationTypes?: FilterCategoryMeta;
  legislationTypes?: FilterCategoryMeta;
}

/** Decision-filter categories that carry pagination meta, and their meta keys. */
const DECISION_PAGINATION_CATEGORIES: Array<
  [lowerFiltersDecisionsEnum, keyof ExtendedFiltersMeta]
> = [
  [lowerFiltersDecisionsEnum.areas, 'areas'],
  [lowerFiltersDecisionsEnum.courts, 'courts'],
  [lowerFiltersDecisionsEnum.judges, 'judges'],
  [lowerFiltersDecisionsEnum.natures, 'natures'],
  [lowerFiltersDecisionsEnum.subAreas, 'subAreas'],
  [lowerFiltersDecisionsEnum.caseLegislationTypes, 'caseLegislationTypes'],
];

/**
 * Reads filter defaults from the 24 h cache, fetching and caching on a miss.
 * The defaults endpoint is the single source of truth for filter defaults.
 */
async function fetchFilterDefaults(): Promise<FilterDefaultsResponse> {
  const cacheKey = 'filter-defaults';
  const cached = filterDefaultsCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const defaultsResponse = await filtersServices.getDefaults();
  // CacheManager handles TTL and cache-entry age internally.
  filterDefaultsCache.set(cacheKey, defaultsResponse.data);
  return defaultsResponse.data;
}

/**
 * Normalizes the API's `decisionTypes` payload, which arrives in two shapes:
 * the old format (plain id strings) and the new one (`{ id, name }` objects).
 * Returns `undefined` when the payload is absent or in neither shape.
 *
 * Shared by the localStorage-persist path and the Redux-defaults path, which
 * previously carried two copies of this conversion.
 */
function normalizeApiDecisionTypes(
  apiDecisionTypes: unknown
): StringFilterItemWithName[] | undefined {
  if (!apiDecisionTypes || !Array.isArray(apiDecisionTypes)) {
    return undefined;
  }
  if (apiDecisionTypes.length > 0 && typeof apiDecisionTypes[0] === 'string') {
    // Old format: plain strings — convert to objects.
    return (apiDecisionTypes as string[]).map((id) => ({
      id: id,
      name: getDecisionTypeLabelStatic(id),
    }));
  }
  if (isStringFilterItemArray(apiDecisionTypes)) {
    // New format: already objects with {id, name}.
    return apiDecisionTypes;
  }
  return undefined;
}

/**
 * Seeds pagination state for one filter category.
 *
 * `data` is deliberately empty — the rows themselves already reached the store
 * via `setFilters`; this only records total/page/page_size so "load more"
 * knows where it is. A category with no meta is skipped.
 */
function dispatchCategoryPagination(
  dispatch: (action: unknown) => void,
  metaData: FilterCategoryMeta | undefined,
  subTab: lowerFiltersDecisionsEnum | 'legislationTypes',
  tab: higherFiltersEnum
): void {
  if (!metaData) {
    return;
  }
  dispatch(
    appendFilterData({
      tab: tab,
      subTab: subTab,
      data: [], // Empty — data is already in state via setFilters
      total: metaData.total || 0,
      page: metaData.page || 1,
      page_size: metaData.page_size || DEFAULT_FILTERS_PAGE_SIZE,
      total_pages: metaData.total_pages || 1,
    })
  );
}

/** Seeds pagination for every filter category the API returned meta for. */
function dispatchMetaPagination(
  dispatch: (action: unknown) => void,
  meta: ExtendedFiltersMeta
): void {
  for (const [subTab, metaKey] of DECISION_PAGINATION_CATEGORIES) {
    dispatchCategoryPagination(
      dispatch,
      meta[metaKey],
      subTab,
      higherFiltersEnum.decisions
    );
  }
  dispatchCategoryPagination(
    dispatch,
    meta.legislationTypes,
    'legislationTypes',
    higherFiltersEnum.legislation
  );
}

/**
 * Persists the API defaults to localStorage for the 24 h cache, alongside the
 * timestamp that drives expiry. Failures are logged, never thrown — a cache
 * write must not break filter initialization.
 */
function persistApiDefaults(backendDefaults: FilterDefaultsResponse): void {
  try {
    const decisionTypesToSave =
      normalizeApiDecisionTypes(backendDefaults.decisions.decisionTypes) ?? [];

    saveApiDefaults({
      searchLegislation: backendDefaults.searchLegislation,
      searchDecisions: backendDefaults.searchDecisions,
      legislation: {
        effectiveAsOf: backendDefaults.legislation.effectiveAsOf || '',
        activeOnly: backendDefaults.legislation.activeOnly ?? true,
        legislationInForceOnly:
          backendDefaults.legislation.legislationInForceOnly ??
          PRE_HYDRATION_LEGISLATION_IN_FORCE_ONLY_DEFAULT, // From API (#7652)
        yearRange: backendDefaults.legislation.yearRange || {
          earliest_publication_date: '',
          latest_publication_date: '',
        },
        legislationTypes: [],
      },
      decisions: {
        decisionTypes: decisionTypesToSave,
        courts: [],
        judges: [],
        areas: [],
        natures: [],
        subAreas: [],
        caseLegislationTypes: [],
        releaseDateRange: backendDefaults.decisions.releaseDateRange || {
          earliest_release_date: '',
          latest_release_date: '',
        },
      },
    });
    updateFiltersTimestamp();
  } catch (error) {
    console.error('❌ Failed to save API defaults:', error);
  }
}

/**
 * Applies every backend-sourced default to the store: pagination meta, special
 * legislation filters, the localStorage cache, decision defaults and the
 * search-scope defaults used by "reset filters".
 */
function applyBackendDefaults(
  dispatch: (action: unknown) => void,
  backendDefaults: FilterDefaultsResponse,
  meta: ExtendedFiltersMeta
): void {
  // Step 4: pagination state per filter category.
  dispatchMetaPagination(dispatch, meta);

  // Step 5: special filters from the backend (SINGLE SOURCE OF TRUTH).
  dispatch(
    setDefaultSpecialFilters({
      activeOnly: backendDefaults.legislation.activeOnly,
      legislationInForceOnly:
        backendDefaults.legislation.legislationInForceOnly, // #7652
      effectiveAsOf: backendDefaults.legislation.effectiveAsOf,
      yearRange: backendDefaults.legislation.yearRange,
    })
  );

  // Save API defaults to localStorage for 24 h caching.
  persistApiDefaults(backendDefaults);

  // Decision defaults (decisionTypes), in either wire format.
  const decisionTypesWithNames = normalizeApiDecisionTypes(
    backendDefaults.decisions.decisionTypes
  );
  dispatch(
    filterSlice.actions.setDefaultDecisionFilters({
      ...(decisionTypesWithNames !== undefined && {
        decisionTypes: decisionTypesWithNames,
      }),
      ...(backendDefaults.decisions.releaseDateRange !== undefined && {
        releaseDateRange: backendDefaults.decisions.releaseDateRange,
      }),
    })
  );

  // Store search scope defaults from API for reset functionality.
  dispatch(
    filterSlice.actions.setDefaultSearchScope({
      searchDecisions: backendDefaults.searchDecisions,
      searchLegislation: backendDefaults.searchLegislation,
    })
  );

  // Set search scope defaults from API.
  dispatch(
    setTotalSelectedFilters({
      searchDecisions: backendDefaults.searchDecisions,
      searchLegislation: backendDefaults.searchLegislation,
    })
  );

  // Always apply default values to state.legislation for UI display, so the UI
  // shows them even when localStorage already exists.
  dispatch(filterSlice.actions.applyDefaultValuesToLegislationState());
}

// Thunk for initializing filters and applying defaults
export const initializeFiltersAndApplyDefaults =
  () => async (dispatch: any) => {
    // Set loading state to true while fetching defaults
    dispatch(filterSlice.actions.setDefaultsLoading(true));

    try {
      // Step 1: Filter defaults from the API (cached, single source of truth).
      const backendDefaults = await fetchFilterDefaults();

      // Step 2: Fetch filter options data (mirrors useFilters.loadFilters).
      const filtersData = await getFilters();

      // Step 2b: Transform legislation types data (as in useFilters).
      const transformedLegislationTypes =
        filtersData.legislation.legislationTypes.map(
          (legislationType: any) => ({
            id: legislationType.id,
            type: legislationType.type,
            name: legislationType.description, // description is the desired name
            description: legislationType.description,
            created_at: '', // Mocked as in useFilters
            updated_at: '', // Mocked as in useFilters
          })
        );

      // Step 3: Dispatch setFilters with the main data
      dispatch(
        setFilters({
          decisions: {
            ...filtersData.decisions,
            releaseDateRange: backendDefaults.decisions.releaseDateRange, // From /filters/defaults API
          },
          legislation: {
            ...filtersData.legislation, // Includes yearRange, activeOnly from API if present
            legislationTypes: transformedLegislationTypes,
          },
        })
      );

      // Steps 4-5: everything the backend defaults drive, only when the
      // response carried the meta block they are keyed off.
      if (filtersData.meta) {
        applyBackendDefaults(
          dispatch,
          backendDefaults,
          filtersData.meta as ExtendedFiltersMeta
        );
      }

      // Step 6: Check if user has custom preferences
      const userPrefs = loadUserPreferences();

      if (!userPrefs) {
        // No user preferences - apply API defaults
        dispatch(filterSlice.actions.applyDefaultSelections());
      } else {
        // User has preferences - they're already in Redux from FilterInitializer
        // Just fill in any missing special filters with defaults
        dispatch(filterSlice.actions.fillMissingSpecialFiltersWithDefaults());
      }
    } catch (error) {
      console.error(
        '[FiltersThunk] Error during filter initialization:',
        error
      );
      // dispatch(setSomeGlobalErrorState(true, 'Failed to initialize filters.'));
    } finally {
      // Set loading state to false when initialization completes
      dispatch(filterSlice.actions.setDefaultsLoading(false));
    }
  };

export const {
  setFilters,
  toggleFilterVisibility,
  toggleFilter,
  toggleDecisionType,
  setJurisdictions,
  // New orthogonal taxonomy actions
  toggleInstitutionalCourt,
  setPublishedOnly,
  // Issue #3724 phase 3 — unified court hierarchy selection
  toggleCourtSelection,

  clearAllFilters,
  setTotalSelectedFilters,
  setYearRange,
  setReleaseDateRange,
  setActiveOnly,
  setLegislationInForceOnly,
  appendFilterData,
  setFilterLoading,
  resetFilterPagination,
  setEffectiveAsOf,
  setDefaultSpecialFilters,

  // Export the new action for search scope defaults

  // Export the new action
  // Export the new action
  restoreFiltersFromLocalStorage, // Export the new action
  // Issue #428: Auto-filter actions
  toggleAutoFilter,

  setRewriteLoading,

  revertAIFilters,
} = filterSlice.actions;
export default filterSlice.reducer;
